/**
 * AI CONTEXT
 * Purpose: the pencil edit affordance on a StandoutMomentCard — a note
 *   textarea, pre-filled from the moment and reset every time it opens (so
 *   a cancel-by-closing never leaves a stale edit armed for next open).
 *   A blank note says "add message" and expands the editor into the card.
 *   On a sentence block the pencil sits beside that sentence and the
 *   editor opens under it. Disabled (shared) shows "Unshare to edit".
 *   Keywords are added in the teacher view, not here. Pure UI/form state;
 *   the actual Firestore write is owned by the card (`onSave`).
 * Used by: StandoutMomentCard (creator or LEAD_TEACHER+, gated by
 *   `canEditMoment`).
 */
import { EditOutlined } from "@ant-design/icons"
import { Button, Input, Popover } from "antd"
import { type ReactNode, useCallback, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"

import ShareLockTooltip from "./ShareLockTooltip"
import { editAffordanceLabel } from "./standoutReviewCardHelpers"
import { useSingleFlight } from "./useSingleFlight"

const { TextArea } = Input

interface MomentEditPopoverProps {
  note: string | null | undefined
  onSave: (patch: { note: string | null }) => Promise<void>
  placeholder?: string
  buttonTestId?: string
  noteTestId?: string
  saveTestId?: string
  /**
   * Keep the button on an action row with `actions`, and open a blank
   * note's editor after that row so it does not push the other buttons down.
   */
  placeEditorBelow?: boolean
  /** Buttons that stay on the same row as Edit, such as Generate. */
  actions?: ReactNode
  /** Shared items cannot be edited until they are unshared. */
  disabled?: boolean
  /** Tooltip on the disabled pencil. Defaults to "Unshare to edit". */
  disabledReason?: string
  /** With `placeEditorBelow`: the sentence the pencil sits beside. */
  leading?: ReactNode
  /** Names the pencil for screen readers, such as "Edit generated note". */
  editLabel?: string
}

const MomentEditPopover = ({
  note,
  onSave,
  placeholder = "Teacher note",
  buttonTestId = "sr-edit-button",
  noteTestId = "sr-edit-note",
  saveTestId = "sr-edit-save",
  placeEditorBelow = false,
  actions,
  disabled = false,
  disabledReason,
  leading,
  editLabel = "Edit",
}: MomentEditPopoverProps) => {
  const [openState, setOpen] = useState(false)
  const open = openState && !disabled
  const [draftNote, setDraftNote] = useState(note ?? "")
  const { pending: isSaving, run: runSave } = useSingleFlight()

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && disabled) return
      if (!nextOpen && isSaving) return
      if (nextOpen) setDraftNote(note ?? "")
      setOpen(nextOpen)
    },
    [disabled, isSaving, note]
  )

  const handleSave = useCallback(
    () =>
      runSave(async () => {
        try {
          const trimmed = draftNote.trim()
          await onSave({ note: trimmed ? trimmed : null })
          setOpen(false)
        } catch (err) {
          // The card already toasted and logged the failed write. Stay open so
          // the teacher can retry, and don't claim the note was saved.
          console.error(
            "[standout] edit popover kept open after a failed save",
            err instanceof Error ? err.message : String(err)
          )
        }
      }),
    [draftNote, onSave, runSave]
  )

  const adding = !note?.trim()

  const editor = (
    <Flex col gap={8} style={{ width: adding ? "100%" : 280 }}>
      <TextArea
        data-testid={noteTestId}
        value={draftNote}
        onChange={(event) => setDraftNote(event.target.value)}
        readOnly={isSaving}
        autoSize={{ minRows: 2, maxRows: 5 }}
        placeholder={placeholder}
      />
      <Button
        data-testid={saveTestId}
        type="primary"
        size="small"
        loading={isSaving}
        onClick={handleSave}
        style={{ alignSelf: "flex-end" }}
      >
        Save
      </Button>
    </Flex>
  )

  const button = (
    <ShareLockTooltip locked={disabled} title={disabledReason}>
      <Button
        data-testid={buttonTestId}
        size="small"
        disabled={disabled}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation()
          if (!adding && !placeEditorBelow) return
          if (isSaving) return
          setDraftNote(note ?? "")
          setOpen((current) => !current)
        }}
        // The empty-note column is full width so the editor can open wide.
        // The button itself stays the width of its icon and label.
        style={{
          width: "fit-content",
          alignSelf: "flex-start",
          pointerEvents: disabled ? "none" : undefined,
        }}
        icon={<EditOutlined />}
        aria-label={adding ? editAffordanceLabel(note) : editLabel}
      >
        {adding ? editAffordanceLabel(note) : null}
      </Button>
    </ShareLockTooltip>
  )

  // On a sentence block the editor opens under that block's own pencil.
  // It is not a sibling of Generate, so the buttons stay put.
  if (placeEditorBelow) {
    return (
      <div
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 8,
          width: "100%",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            minWidth: 0,
          }}
        >
          {leading ? (
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>{leading}</div>
          ) : null}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              flex: "0 0 auto",
            }}
          >
            {button}
            {actions}
          </div>
        </div>
        {open ? (
          <AnimateInFlex
            animation={ANIMATION_BY_KEY.down}
            col
            gap={8}
            style={{ width: "100%" }}
          >
            {editor}
          </AnimateInFlex>
        ) : null}
      </div>
    )
  }

  // A missing note expands the editor into the card. An existing note keeps
  // the popover so Edit does not push the row open.

  if (adding) {
    return (
      <Flex col gap={8} style={{ width: "100%" }}>
        {button}
        {open ? (
          <AnimateInFlex
            animation={ANIMATION_BY_KEY.down}
            col
            gap={8}
            style={{ width: "100%" }}
          >
            {editor}
          </AnimateInFlex>
        ) : null}
      </Flex>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      content={editor}
    >
      {button}
    </Popover>
  )
}

export default MomentEditPopover
